import { defineStore } from "pinia";
import { db } from "@/firebase";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { createPrefixedLogger } from "@/utils/logger";

const logger = createPrefixedLogger("CONFIG");

// Default config values (used if Firestore config doesn't exist)
const DEFAULT_CONFIG = {
  adminEmails: [],
  activeSurveyId: null,
  features: {
    emailLinkAuth: true,
    surveyImageRequired: false,
    surveyLocationRequired: false,
  },
};

export const useConfigStore = defineStore("config", {
  state: () => ({
    adminEmails: [],
    activeSurveyId: null,
    features: { ...DEFAULT_CONFIG.features },
    loaded: false,
    loading: false,
    _loadPromise: null,
  }),

  actions: {
    async fetchConfig(forceRefresh = false) {
      // Return if already loaded (unless force refresh)
      if (this.loaded && !forceRefresh) {
        return;
      }

      // If already loading, wait for the existing promise
      if (this.loading && this._loadPromise) {
        return this._loadPromise;
      }

      this.loading = true;
      this._loadPromise = this._doFetchConfig();
      return this._loadPromise;
    },

    async _doFetchConfig() {

      try {
        logger.debug("Fetching app config from Firestore");
        const configDoc = doc(db, "config", "app");
        const docSnap = await getDoc(configDoc);

        if (docSnap.exists()) {
          const data = docSnap.data();
          this.adminEmails = data.adminEmails || DEFAULT_CONFIG.adminEmails;
          this.activeSurveyId = data.activeSurveyId || DEFAULT_CONFIG.activeSurveyId;
          this.features = { ...DEFAULT_CONFIG.features, ...data.features };
          logger.debug("Config loaded:", { admins: this.adminEmails.length, survey: this.activeSurveyId });
        } else {
          // Create default config document
          logger.info("Creating default config document");
          await setDoc(configDoc, DEFAULT_CONFIG);
          this.adminEmails = DEFAULT_CONFIG.adminEmails;
          this.activeSurveyId = DEFAULT_CONFIG.activeSurveyId;
          this.features = { ...DEFAULT_CONFIG.features };
        }

        this.loaded = true;
      } catch (error) {
        logger.error("Error fetching config:", error);
        // Use defaults on error
        this.adminEmails = DEFAULT_CONFIG.adminEmails;
        this.features = { ...DEFAULT_CONFIG.features };
      } finally {
        this.loading = false;
      }
    },

    isAdmin(email) {
      if (!email) return false;
      return this.adminEmails.some(
        (adminEmail) => adminEmail.toLowerCase() === email.toLowerCase()
      );
    },

    async addAdmin(email) {
      if (!email || this.isAdmin(email)) return false;

      try {
        this.adminEmails.push(email.toLowerCase());
        await this.saveConfig();
        logger.info("Admin added:", email);
        return true;
      } catch (error) {
        logger.error("Error adding admin:", error);
        // Rollback
        this.adminEmails = this.adminEmails.filter(
          (e) => e.toLowerCase() !== email.toLowerCase()
        );
        throw error;
      }
    },

    async removeAdmin(email) {
      if (!email || !this.isAdmin(email)) return false;

      try {
        this.adminEmails = this.adminEmails.filter(
          (e) => e.toLowerCase() !== email.toLowerCase()
        );
        await this.saveConfig();
        logger.info("Admin removed:", email);
        return true;
      } catch (error) {
        logger.error("Error removing admin:", error);
        throw error;
      }
    },

    async saveConfig() {
      try {
        const configDoc = doc(db, "config", "app");
        await setDoc(configDoc, {
          adminEmails: this.adminEmails,
          activeSurveyId: this.activeSurveyId,
          features: this.features,
        });
        logger.debug("Config saved");
      } catch (error) {
        logger.error("Error saving config:", error);
        throw error;
      }
    },

    async setActiveSurvey(surveyId) {
      this.activeSurveyId = surveyId;
      await this.saveConfig();
      logger.info("Active survey set to:", surveyId);
    },
  },

  getters: {
    isLoaded: (state) => state.loaded,
    adminCount: (state) => state.adminEmails.length,
    hasSurvey: (state) => state.activeSurveyId !== null,
  },
});
